/**
 * SPEC.md §7, §9 — the topbar's About menu, issue #32 (M6.5). Mirrors
 * `ShareMenu.tsx`'s shape, built on the same `useMenu` hook (M5.1, issue
 * #21). §7's own line: "Add a 'Clear all local data' item in the About
 * menu" — wipes `plotter:draft` and `plotter:history`, the two
 * localStorage keys §7 defines, behind a confirmation step so a stray
 * click can't destroy 20 diagrams' worth of history. The confirmation
 * copy lives in `commands.ts` (`CLEAR_ALL_DATA_CONFIRM_MESSAGE`) — M6.8
 * (issue #35)'s command palette shows the exact same text before it, too,
 * since that confirmation step is UI state this component owns, not part
 * of `onClearAllData` itself.
 */
import { useEffect, useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import { CLEAR_ALL_DATA_CONFIRM_MESSAGE } from '../lib/commands';
import { useMenu } from './useMenu';

interface AboutMenuProps {
  /** Wipes plotter:draft and plotter:history, and shows a §9-style confirmation. */
  onClearAllData: () => void;
}

export default function AboutMenu({ onClearAllData }: AboutMenuProps) {
  const { isOpen, containerRef, toggle, close } = useMenu<HTMLDivElement>();
  const [confirming, setConfirming] = useState(false);

  // Confirming is local UI state, not tracked by useMenu — reset it
  // whenever the menu closes (outside click, Escape, or a completed
  // action) so reopening About never starts mid-confirmation.
  useEffect(() => {
    if (!isOpen) setConfirming(false);
  }, [isOpen]);

  function handleConfirm() {
    onClearAllData();
    close();
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggle}
        className="flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-text-ink hover:bg-ink-3"
      >
        <Info size={13} aria-hidden />
        About
        <ChevronDown size={12} aria-hidden />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="About"
          className="absolute right-0 top-full z-10 mt-1 w-64 rounded-sm border border-ink-3 bg-ink-2 py-1 shadow-lg"
        >
          {confirming ? (
            <div className="px-3 py-1.5">
              <p className="text-xs text-text-ink">{CLEAR_ALL_DATA_CONFIRM_MESSAGE}</p>
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="rounded-sm px-2 py-1 text-xs text-mute hover:bg-ink-3 hover:text-text-ink"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="rounded-sm px-2 py-1 text-xs text-pink hover:bg-ink-3"
                >
                  Clear all local data
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="px-3 py-1.5 text-xs text-mute">
                Plotter is a static Mermaid diagram previewer. Nothing you type — no diagram source, no
                exported file — ever leaves this device. Page visits are tracked with Google Analytics.
              </p>
              <p className="px-3 pb-1.5 text-xs text-mute">
                Large flowchart tangled? Add{' '}
                <code className="font-mono text-text-ink">{'layout: elk'}</code> to a{' '}
                <code className="font-mono text-text-ink">config</code> frontmatter block for a cleaner
                auto-layout.
              </p>
              <div className="my-1 border-t border-ink-3" />
              <button
                type="button"
                role="menuitem"
                onClick={() => setConfirming(true)}
                className="block w-full px-3 py-1.5 text-left text-xs text-text-ink hover:bg-ink-3"
              >
                Clear all local data
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
